import type { FunctionComponent, HTMLProps } from 'react'
import React, { createElement } from 'react'

import classnames from 'classnames'

import type { ImageSource } from '../../../types'
import { Author } from '../../Author'
import { Link } from '../../Link'
import { LinksListExpandable } from '../../LinkBlock'
import { BaseLink } from '../../Links'
import { Picture } from '../../Picture'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './ArticleCard.module.scss'
import { ArticleCardText } from './ArticleCardText'

export const ArticleCard: FunctionComponent<ArticleCardProps> = ({
  authorImage,
  authorImageSources,
  authorName,
  clampStrapline = true,
  className,
  date,
  display,
  href,
  image,
  imageAlt,
  imageSources,
  lazyLoad = true,
  includeCommentsIcon = false,
  linksListExpandableData,
  mainLinkProps,
  orientation,
  shortenedDate,
  singleLinkData,
  strapline,
  title,
  tag = 'article',
  ...rest
}) => {
  const isHorizontal = orientation === 'horizontal'

  const props = {
    className: classnames(
      styles.articleCard,
      display === 'scroll-row' && styles.articleCardScrollRow,
      isHorizontal && styles.horizontalContainer,
      !image && isHorizontal && styles.articleCardBorderBottom,
      'sb-link-active-reset',
      className
    ),
    ...rest,
  }

  const children = (
    <>
      {shortenedDate && (
        <TypographyV2
          className={styles.shortenedDate}
          textStyle="sb-text-interface-body-small-regular"
        >
          {shortenedDate}
        </TypographyV2>
      )}
      {image && (
        <BaseLink
          href={href}
          className={classnames({
            [styles.horizontalCardImageLink]: isHorizontal,
          })}
          aria-label={imageAlt || title}
          {...mainLinkProps}
        >
          <Picture
            alt={imageAlt || ''}
            aspectRatioMain="two-to-one"
            sources={imageSources}
            src={image}
            lazyLoad={lazyLoad}
          />
        </BaseLink>
      )}
      <ArticleCardText
        className={classnames(
          isHorizontal && styles.horizontalCardText,
          clampStrapline && styles.clampCardText,
          singleLinkData && styles.singleLink
        )}
        date={date}
        href={href}
        includeCommentsIcon={includeCommentsIcon}
        strapline={strapline}
        title={title}
        linkProps={mainLinkProps}
      >
        {authorName && (
          <Author
            name={authorName}
            src={authorImage}
            sources={authorImageSources}
            isHorizontal={isHorizontal}
          />
        )}
        {singleLinkData &&
          (singleLinkData.disabled ? (
            <TypographyV2 textStyle="sb-text-interface-body-small-regular">
              {singleLinkData.text}
            </TypographyV2>
          ) : (
            <Link
              href={singleLinkData.href}
              className={styles.optionalLink}
              textStyle="sb-text-interface-body-small-regular"
              data-testid="article-card-single-link"
            >
              {singleLinkData.text}
            </Link>
          ))}
        {linksListExpandableData && (
          <LinksListExpandable {...linksListExpandableData} />
        )}
      </ArticleCardText>
    </>
  )

  return createElement(tag, props, children)
}

///////// IMPLEMENTATION /////////

export type ArticleCardProps = {
  /**
   * Image that will appear in the Roundel to the left of the author's name. If none is supplied their initials will be used instead.
   */
  authorImage?: string
  /**
   * Author image data for responsive images.
   */
  authorImageSources?: ImageSource[]
  /**
   * Authors name to be included at the bottom of the article card.
   */
  authorName?: string
  /**
   * Props to be passed through to both BaseLink components (i.e. the links to the main href)
   * E.g. for data properties for tracking purposes
   */
  mainLinkProps?: React.ComponentPropsWithoutRef<typeof BaseLink>
  /**
   * When passing a strapline by default we use css line clamp to only show some of the text.
   * Passing false will disable this behaviour and show all of the strapline provided.
   */
  clampStrapline?: boolean
  /**
   * Class name that can be added to add extra styling to the component
   */
  className?: string
  /**
   * Article date to appear at the bottom of the card. N.B This will not appear if an author prop is also provided.
   */
  date?: string
  /**
   * Display format affects layout of the card. Use 'scroll-row' when inside CardRow
   */
  display?: string
  /**
   * Link that will be visited when the card is clicked on.
   */
  href: string
  /**
   * Image link to be displayed at the top of the article card if provided.
   */
  image?: string
  /**
   * Text to be used in the Image alt tag.
   */
  imageAlt?: string
  /**
   * Article image data for responsive images.
   */
  imageSources?: ImageSource[]
  lazyLoad?: boolean
  /**
   * Article comments icon - Optionally renders next to the above date prop. N.B requires author and date prop.
   */
  includeCommentsIcon?: boolean
  /**
   * Optionally renders a list of links via the LinksListExpandable component.
   */
  linksListExpandableData?: Parameters<typeof LinksListExpandable>[0]
  /**
   * Layout direction for the card
   */
  orientation?: string
  /**
   * Optional shorter date string.
   */
  shortenedDate?: string
  /**
   * Optional link object containing a href and text values. If disabled is true, text value will be shown with no link.
   */
  singleLinkData?: {
    href: string
    text: string
    disabled?: boolean
  } | null
  /**
   * Strapline that will appear below the title.
   */
  strapline?: string
  /**
   * Outermost HTML element to be rendered. Defaults to 'article'.
   */
  tag?: keyof JSX.IntrinsicElements
  /**
   * Title that will appear within the article card.
   */
  title: string
} & HTMLProps<HTMLElement>
